import type { FormQuestion, FormQuestionSet } from '@mds/shared/types/form-question';
import { Field } from '@mds/shared/ui/form/field';
import { FormSection } from '@mds/shared/ui/form/form-section';
import { QuestionInput } from '@mds/shared/ui/form/question-input';

const bySortOrder = <T extends { sortOrder: number }>(list: readonly T[]) =>
  [...list].sort((a, b) => a.sortOrder - b.sortOrder);

/**
 * The questions staff added to an event form — the registration form or the
 * feedback form — as a run of cards: the opening card first, then a card per
 * section under its own heading.
 *
 * The opening card holds the page's own fields (`children`) followed by the
 * questions outside any section, so those never get a card with no title of
 * their own. It is left out when there is nothing to put in it.
 *
 * Returns the cards side by side in a fragment, so the `<Form>` around them
 * sets the gap between them, the same gap as between every other form's cards.
 *
 * Every question is a `Field` bound to `<name>.<question id>`, so it must sit
 * inside a `<Form>` whose values hold that entry for each question —
 * `blankAnswers(form)` from `@mds/shared/lib/form-answers` builds them.
 */
export default function FormQuestions({
  form,
  name = 'answers',
  title,
  description,
  children,
  footer,
}: {
  form: FormQuestionSet;
  /** Where the answers live in the form's values. */
  name?: string;
  /** The opening card's title: "Your details". */
  title?: string;
  description?: string;
  /** The page's own fields, at the top of the opening card. */
  children?: React.ReactNode;
  /** Closes the last card, whichever it is: a consent, an opt-in. */
  footer?: React.ReactNode;
}) {
  const sections = bySortOrder(form.sections);
  const field = (question: FormQuestion) => (
    <Field
      key={question.id}
      name={`${name}.${question.id}`}
      label={question.label}
      required={question.required}
    >
      <QuestionInput rich question={question} />
    </Field>
  );

  return (
    <>
      {(children || form.ungroupedQuestions.length > 0 || (footer && sections.length === 0)) && (
        <FormSection title={title} description={description}>
          {children}
          {bySortOrder(form.ungroupedQuestions).map(field)}
          {sections.length === 0 && footer}
        </FormSection>
      )}

      {sections.map((section, i) => (
        <FormSection
          key={section.id}
          title={section.title}
          description={section.description || undefined}
        >
          {bySortOrder(section.questions).map(field)}
          {i === sections.length - 1 && footer}
        </FormSection>
      ))}
    </>
  );
}
